import type {AggregationJson, AggregationResult} from "./Aggregation.js";
import type {FilterJson} from "./Filter.js";
import type {JsonObject, JsonValue} from "./types.js";

export interface ListMetadata {
    readonly totalCount?: number;
    readonly aggregations?: readonly AggregationResult[];
}

export interface Envelope<TData> {
    readonly data: TData;
    readonly metadata: ListMetadata;
}

/**
 * Free-text search. Unlike a filter this also orders the result, putting the closest match
 * first, which is why the backend takes it as its own header.
 */
export interface SuggestionFilterJson {
    readonly searchString: string;
    readonly fields: readonly string[];
    readonly orderBy: readonly string[];
}

export interface ListQuery {
    readonly where?: FilterJson;
    readonly orderBy?: string;
    readonly maxRows?: number;
    readonly firstRow?: number;
    readonly appId?: number;
    readonly suggestionFilter?: SuggestionFilterJson;
    readonly aggregations?: readonly AggregationJson[];
}

export class RestError extends Error {
    constructor(readonly status: number, readonly url: string, readonly body: JsonValue) {
        super(`${url} responded ${status}${typeof body === "object" && body !== null && "error" in body
            ? `: ${String((body as JsonObject)["error"])}`
            : ""}`);
        this.name = "RestError";
    }
}

export interface RestClientOptions {
    /** Prefix for every path, `/api` for the hewecon backend. */
    readonly basePath?: string;
    readonly frontendVersion?: string;
    readonly fetch?: typeof fetch;
    /** Attempts per request when the network itself fails. */
    readonly retries?: number;
    readonly retryDelayMs?: number;
}

const listHeaders = (query: ListQuery): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (query.where !== undefined) {
        headers["Where"] = encodeURIComponent(JSON.stringify(query.where));
    }
    if (query.orderBy !== undefined) {
        headers["Order-By"] = encodeURIComponent(query.orderBy);
    }
    if (query.maxRows !== undefined) {
        headers["Max-Rows"] = String(query.maxRows);
    }
    if (query.firstRow !== undefined) {
        headers["First-Row"] = String(query.firstRow);
    }
    if (query.appId !== undefined) {
        headers["App-Id"] = String(query.appId);
    }
    if (query.suggestionFilter !== undefined) {
        headers["Suggestion-Filter"] = encodeURIComponent(JSON.stringify(query.suggestionFilter));
    }
    if (query.aggregations !== undefined && query.aggregations.length > 0) {
        headers["aggregations"] = encodeURIComponent(JSON.stringify(query.aggregations));
    }
    return headers;
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * A list entry. The backend elides an entity to a bare id when the same entity was already
 * serialized elsewhere in the response, so a list is not always a list of objects.
 */
export type ListItem = JsonObject | number;

/**
 * The only place that speaks HTTP. It knows the envelope, the list header protocol and the
 * error shape; everything above it works with parsed payloads.
 */
export class RestClient {
    readonly #basePath: string;
    readonly #fetch: typeof fetch;
    readonly #headers: Record<string, string>;
    readonly #retries: number;
    readonly #retryDelayMs: number;

    constructor(options: RestClientOptions = {}) {
        this.#basePath = options.basePath ?? "/api";
        this.#fetch = options.fetch ?? ((input, init) => fetch(input, init));
        this.#headers = {"Accept": "application/json"};
        if (options.frontendVersion !== undefined) {
            this.#headers["frontend-version"] = options.frontendVersion;
        }
        this.#retries = options.retries ?? 3;
        this.#retryDelayMs = options.retryDelayMs ?? 500;
    }

    async getList(path: string, query: ListQuery = {}): Promise<Envelope<ListItem[]>> {
        const envelope = await this.#request<JsonValue>("GET", path, {headers: listHeaders(query)});
        return {
            data: Array.isArray(envelope.data) ? envelope.data as ListItem[] : [],
            metadata: envelope.metadata
        };
    }

    async getOne(path: string): Promise<JsonObject> {
        const envelope = await this.#request<JsonObject>("GET", path);
        return envelope.data;
    }

    post(path: string, body: JsonValue): Promise<Envelope<JsonObject>> {
        return this.#request<JsonObject>("POST", path, {body});
    }

    put(path: string, body: JsonValue): Promise<Envelope<JsonObject>> {
        return this.#request<JsonObject>("PUT", path, {body});
    }

    patch(path: string, body: JsonValue): Promise<Envelope<JsonObject>> {
        return this.#request<JsonObject>("PATCH", path, {body});
    }

    delete(path: string): Promise<Envelope<JsonObject>> {
        return this.#request<JsonObject>("DELETE", path);
    }

    async #request<TData>(
        method: string,
        path: string,
        options: { headers?: Record<string, string>; body?: JsonValue } = {}
    ): Promise<Envelope<TData>> {
        const url = `${this.#basePath}${path.startsWith("/") ? path : `/${path}`}`;
        const headers = {...this.#headers, ...options.headers};
        if (options.body !== undefined) {
            headers["Content-Type"] = "application/json";
        }
        const response = await this.#fetchWithRetry(url, {
            method,
            headers,
            credentials: "same-origin",
            ...(options.body === undefined ? {} : {body: JSON.stringify(options.body)})
        });
        const payload = await readJson(response);
        if (!response.ok) {
            throw new RestError(response.status, url, payload);
        }
        return toEnvelope<TData>(payload);
    }

    /** Retries only transport failures; a response, even a failing one, is an answer. */
    async #fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
        let lastError: unknown;
        for (let attempt = 0; attempt < this.#retries; attempt++) {
            try {
                return await this.#fetch(url, init);
            } catch (error) {
                lastError = error;
                if (attempt < this.#retries - 1) {
                    await delay(this.#retryDelayMs * (attempt + 1));
                }
            }
        }
        throw lastError;
    }
}

const readJson = async (response: Response): Promise<JsonValue> => {
    const text = await response.text();
    if (text.length === 0) {
        return {};
    }
    try {
        return JSON.parse(text) as JsonValue;
    } catch {
        return {error: text};
    }
};

const toEnvelope = <TData>(payload: JsonValue): Envelope<TData> => {
    if (typeof payload === "object" && payload !== null && !Array.isArray(payload) && "data" in payload) {
        const envelope = payload as { data: JsonValue; metadata?: JsonValue };
        return {
            data: envelope.data as TData,
            metadata: (envelope.metadata ?? {}) as ListMetadata
        };
    }
    return {data: payload as TData, metadata: {}};
};
